import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function Economics33() {

   const contentRef = useRef(null);
         const links = {
        prev: "/economics-32", 
        index: "/index-economics",                 
        next: "/economics-34", 
      };
        const titleText ="Misconception of Forced Labor Division";
        const questions = [
  {
    text: "1. What is one major consequence of forced division of labor in centrally planned economies?",
    options: [
      { value: "A", label: "A. Increased individual freedom and innovation" },
      { value: "B", label: "B. Higher rates of voluntary job satisfaction" },
      { value: "C", label: "C. Inefficiency and misallocation of human resources" },
      { value: "D", label: "D. Optimal alignment between skills and roles" },
    ],
    correct: "C",
  },
  {
    text: "2. In contrast to forced labor assignment, what does voluntary labor specialization rely on?",
    options: [
      { value: "A", label: "A. Government quotas and production targets" },
      { value: "B", label: "B. Tradition and social hierarchies" },
      { value: "C", label: "C. Individual choices and market signals" },
      { value: "D", label: "D. Random selection and manual assignment" },
    ],
    correct: "C",
  },
  {
    text: "3. Which of the following is a likely outcome when labor is allocated by coercion rather than market demand?",
    options: [
      { value: "A", label: "A. Enhanced creativity and entrepreneurial activity" },
      { value: "B", label: "B. Decreased economic output and worker morale" },
      { value: "C", label: "C. Increased competition and specialization" },
      { value: "D", label: "D. Greater efficiency in resource allocation" },
    ],
    correct: "B",
  },
  {
    text: "4. Why is forced division of labor considered economically harmful?",
    options: [
      { value: "A", label: "A. It encourages people to become too independent" },
      { value: "B", label: "B. It prevents businesses from outsourcing" },
      {
        value: "C",
        label: "C. It ignores the natural diversity of talents and preferences",
      },
      { value: "D", label: "D. It leads to excessive automation" },
    ],
    correct: "C",
  },
];
 // State to track selected answers for all questions
      const [answers, setAnswers] = useState({});
    
      // Function to update an answer
      const handleAnswerChange = (questionIndex, selectedValue) => {
        setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
      };
    
      // Compute score
      const score = questions.reduce((total, q, index) => {
        return total + (answers[index] === q.correct ? 1 : 0);
      }, 0);

  return (
    <div className="container">
    
     <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={33}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 33</b>!
Today’s lesson challenges one of the most persistent and emotionally appealing myths in economics: the idea that jobs must be artificially created, divided, or preserved at all costs. We will examine why employment is not an end in itself, but a means to the greater goal of productive wealth creation. By exploring the fallacy of forced labor division, the push for shorter workweeks, and the mistaken belief that the supply of work is fixed, this lesson will sharpen your understanding of efficiency, opportunity cost, and the unseen consequences of well-intentioned policies. You will learn how genuine prosperity emerges not from spreading work thin, but from allowing productivity, innovation, and human choice to expand economic opportunity for all.<br/><br/>
<b>Decoding The Fallacy of Forced Job Creation
</b><br/><br/>
        The idea that work should be forcibly divided among multiple people to
        "create employment" is a widespread economic fallacy. This belief
        manifests in various forms, most visibly in the rules enforced by labor
        unions. In many regions, union contracts prevent a single worker from
        performing multiple tasks, even if they are capable of doing so. For
        example, a plumber fixing a leaky pipe in a bathroom might not be
        allowed to replace a damaged tile afterward. Instead, a separate worker
        i.e a tile-setter must be hired. On the surface, it appears that two
        people are employed, and therefore, two incomes are generated. But this
        view fails to account for the broader economic consequences.
        <br />
        Let’s analyze what truly happens in such a case. The homeowner has a
        limited budget. If he could have paid the plumber to fix both the pipe
        and the tile, he would have saved money that he could have used
        elsewhere in the economy: at a local restaurant, a clothing shop, or a
        bookstore. That surplus would have circulated through the market and
        employed more people in diverse sectors. But because he had to pay two
        workers instead of one for a single task, he ends up with less
        disposable income. This reduces his ability to consume other goods and
        services.
        <br />
        The fallacy here is a focus on visible employment rather than productive
        efficiency. While it is easy to see that two workers have been employed,
        it is harder to see the unseen costs: the jobs that are not created in
        other sectors because the consumer’s money was already exhausted due to
        enforced inefficiencies. In economic terms, this is a misallocation of
        resources. Time, energy, and money are diverted from productive uses to
        fulfill artificial employment quotas. Instead of promoting true wealth
        creation, such practices stifle growth and reduce overall well-being.
        <br />
        Labor, like capital, must be deployed efficiently to maximize its
        output. If we force labor into tasks that could be handled more simply
        or more cheaply, we are wasting not only money but also the productive
        potential of those workers. A plumber who could have completed both jobs
        efficiently is now artificially limited. A tile-setter who is called in
        for a trivial task is not necessarily gaining meaningful employment;
        instead, the opportunity cost of that employment is borne by the broader
        economy in the form of lost consumption and inefficient spending.
        <br />
        <br />
        <b>The Push for Shorter Workweeks</b>
        <br />
        <br />
        Another modern version of this fallacy lies in the growing calls for
        shorter workweeks. Across the globe, especially in developed countries,
        voices are rising in favor of reducing the number of working days to
        four per week, or to reduce total working hours to 30 instead of the
        standard 40 or more. The argument is emotionally compelling: by limiting
        each person’s work hours, the total amount of work will remain the same
        and will need to be divided among more people thus reducing
        unemployment.
        <br />
        To ensure that workers are not financially harmed by this reduction in
        work hours, advocates of this policy often suggest that hourly wages be
        increased, so that people can earn the same income in fewer hours. While
        it seems compassionate and just, this policy introduces severe
        inefficiencies. First, by artificially increasing wages and reducing
        work time, we are raising the cost of production for every good and
        service. This is not a theoretical problem; it manifests as increased
        prices for everything from groceries to housing to transportation.
        <br />
        Businesses with narrow profit margins find themselves squeezed. Many are
        forced to either raise prices, lay off workers, outsource, or shut down
        altogether. The less efficient businesses, which may have been providing
        localized or niche services, are the first to go under. The less skilled
        or less productive workers, who cannot justify their new artificially
        high wages, are laid off. Thus, while the policy intends to help workers
        and create more jobs, its real-world impact often includes job loss,
        business closures, and a higher cost of living especially hurting those
        on the economic margins.
        <br />
        The idea of reducing work to spread it around assumes a zero-sum view of
        the economy: that there is a fixed pool of work that must be distributed
        equally. But this is simply not how economies function. The amount of
        work in society expands or contracts based on innovation, consumer
        demand, entrepreneurship, and productivity. Policies that artificially
        restrict hours ignore this dynamism. Rather than increasing the total
        output of the economy, they reduce it, leading to stagnation and
        hardship, especially for the poor.
        <br />
        History teaches us that prosperity arises not from reducing productivity
        but from increasing it. Encouraging people to work less while expecting
        to maintain or increase their standard of living is not just naive,
        rather it’s economically destructive. A wealthy society is built on the
        backs of productive individuals who work hard, innovate, take risks, and
        serve others. Punishing productivity in the name of equity undermines
        the very foundation of that wealth.
        <br />
        <br />
        <b>The Myth of Fixed Work Supply</b>
        <br />
        <br />
        At the heart of both these errors, the forced division of tasks and
        shorter work weeks is the deeply flawed assumption that the amount of
        work in society is fixed. This fallacy is widespread and intuitive:
        people imagine that if a certain number of jobs exist, then they must be
        divided among a certain number of people. If there are not enough jobs,
        unemployment must follow.
        <br />
        But economies are not static machines with preset dials. They are
        living, breathing systems, constantly adjusting to needs, wants, skills,
        technologies, and innovations. One of the best examples that destroys
        the fixed-work-supply myth comes from post-war societies. When wars end,
        millions of soldiers are released from duty. These soldiers had been
        fully employed during wartime, they were fed, clothed, and paid by the
        government. The fear often arises that they will flood the job market
        and overwhelm the private sector.
        <br />
        However, what actually happens tells a different story. Once governments
        stop paying wages to soldiers, citizens are no longer taxed to support
        those soldiers. That money is freed up to be spent in the private
        sector: at shops, cafes, industries, and services. This stimulates
        demand, which leads to more production and, therefore, more jobs.
        Soldiers find work not because new jobs were "waiting" for them, but
        because the freed-up capital in society generated new economic
        opportunities.
        <br />
        This phenomenon reveals a vital truth: jobs are created by production
        and consumption, not by mere redistribution. When people spend money on
        things they value, businesses respond by producing more, hiring more,
        and investing more. The total supply of work grows in response to human
        wants and needs. The notion that labor must be carved up and shared in a
        rigid, predefined pie is as outdated as it is damaging.
        <br />
        Every job must ultimately be paid for by someone’s willingness to pay
        for the good or service produced. Artificially inflating the number of
        jobs without corresponding increases in value generation creates
        inefficiencies, inflation, and decline. Instead of more people doing
        less meaningful work, what we need is fewer people doing more
        meaningful, high-impact work which creates the wealth that raises the
        standard of living for all.
        <br />
        <br />
        <b>Employment Is the Means and not the Goal</b>
        <br />
        <br />
        <br />
        Perhaps the most dangerous assumption made by advocates of forced labor
        division is that employment itself is the goal of an economy. But this
        is not true. Employment is a means to a more fundamental end:
        production. We do not work simply for the sake of being busy; we work to
        produce goods and services that improve human life.
        <br />
        When employment is treated as the ultimate goal, economic policies
        become warped. Governments begin to subsidize meaningless jobs, prop up
        failing industries, and create incentives for busywork. This is not
        prosperity; it is stagnation disguised as progress. A society with 100
        percent employment in meaningless or inefficient jobs is not better off
        than one with lower employment but higher productivity, technological
        advancement, and wealth creation.
        <br />
        Consider this: if we truly wanted to maximize employment regardless of
        output, we could ban tractors and bring back hand tools for farming.
        Instantly, millions of people would need to work in agriculture just to
        feed the population. But would this make us richer? Of course not. It
        would make us poorer, because much of our labor would be wasted on tasks
        that could be done more efficiently with machines. People would spend
        more time growing food and less time writing books, building software,
        or caring for children.
        <br />
        The real goal is maximum production using minimal resources. When we
        pursue that goal, labor becomes more valuable, not less. Children, the
        elderly, and those who genuinely cannot work are spared from being
        pushed into jobs they don’t need to do. Society becomes wealthier,
        freer, and more humane.
        <br />
        This understanding helps us see why many political slogans about "full
        employment" are not just wrong; they are dangerous. They obscure the
        real purpose of economic activity and lead to policies that hurt the
        very people they claim to help. A better approach is to focus on
        productivity, efficiency, and innovation, and allow employment to grow
        organically as a result of human creativity and market demand.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today’s lesson explored the myth of forced division of labor and its
        harmful impact on the economy. We discussed how policies that
        artificially divide work among people whether through union rules,
        reduced work hours, or political mandates often lead to inefficiencies,
        waste, and lower overall prosperity. Labor is a valuable resource that
        should be used productively, not rationed to create the illusion of full
        employment. We also debunked the fallacy that the total amount of work
        in society is fixed, showing instead that economic freedom and
        production naturally create new jobs. Ultimately, the goal of an economy
        should not be employment for its own sake, but full and efficient
        production that leads to wealth and human flourishing.
        <br />
        <br /> <b> Coming Up Next</b>
        <br />
        <br />
        Next we will take a close look at the idea of price parity. a policy
        often promoted as a way to protect farmers but one that creates more
        harm than good. We’ll explore how pegging the prices of agricultural
        goods to those of industrial products distorts market signals, leads to
        artificial scarcity, and ends up punishing consumers. You’ll see how
        well-meaning interventions often result in unfair advantages, wasted
        resources, and a net loss to the overall economy. Stay with us as we
        unpack the long-term consequences of this misunderstood approach to
        economic equality.
      </p>
      <h2>Test your understanding</h2>
       {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics33;
